/* eslint-disable node/no-missing-require */
const Model = require("../types/Model.js");
const { UserMetrics } = require("@aetonix/user-metrics");
const { Identify } = require("@amplitude/analytics-react-native");
const { Platform, Dimensions } = require("react-native");

const BASE_IGNORED_EVENTS = [
	"localization:trigger:set",
	"login:trigger:show",
	"login:trigger:asked",
	"passwordtimeout:trigger:reset",
	"app:trigger:register",
	"keyboard:trigger:close",
	"keyboard:trigger:open",
	"usermetrics:trigger:track",
	"login:trigger:login",
	"activeworkflowaction:trigger:hide",
	"activeworkflowaction:trigger:view",
	"activeworkflowaction:trigger:submit",
	"preferences:trigger:view",
	"desktop:trigger:page",
	"personaprofilepicture:trigger:start",
	"personaprofilepicture:trigger:cancel",
	"personaprofilepicture:trigger:crop",
];

module.exports = Model({
	name: "usermetrics",
	init,
	uninit,
	listeners,
	triggers,
	refresh,

	_userMetrics: null,
	_eventQueue: [],
	_ignoredEvents: BASE_IGNORED_EVENTS,
	_ready: false,
});

async function init() {
	await this.refresh();

	const config = this.config;
	const { user_metrics_url } = config;
	const options = user_metrics_url ? { user_metrics_url } : {};
	this._userMetrics = new UserMetrics({
		user_metrics_key: config.user_metrics_key,
	}, null, options);

	const user = new Identify();
	if (config.id) {
		this._userMetrics.setUserId(config.id);
	} else {
		// When the user id is set to null, amplitude will use the device id
		this._userMetrics.setUserId(undefined);
	}

	// Set once (these cannot be changed once set)
	user.setOnce("build", config.build)
		.setOnce("version", config.version)
		.setOnce("stage", config.type)
		.setOnce("platform", Platform.OS)
		.setOnce("region", config.name || "Unknown");

	// Set (these can be changed if a user logs out, etc)
	user.set("_id", config.id)
		.set("user id", config.id)
		.set("token", config.token)
		.set("user types", config.user_type)
		.set("organization", config.organization)
		.set("org group", config.orgGroup)
		.set("initial screen width", Dimensions.get("window").width)
		.set("initial screen height", Dimensions.get("window").height);

	try {
		this._userMetrics.identifyUser(user);
	} catch (error) {
		console.error("There was an error while identifying the user", error);
	}

	config.organization && this._userMetrics.assignUserToGroup("organization", config.organization);
	config.orgGroup && this._userMetrics.assignUserToGroup("org group", config.orgGroup);
	this._userMetrics.assignUserToGroup("region", config.name || "Unknown");

	// Send any events that were queued up before the user metrics instance was initialized
	for (const event in this._eventQueue) {
		trackEvent(event, this._eventQueue[event]);
	}

	this._ready = true;
}

function uninit() {
	this._userMetrics = null;
}

function triggers() {
	return {
		"track": trackEvent,
		"identify": setUserProperty,
		"identify:immutable": setImmutableUserProperty,
		"identify:id": setUserId,
		"opt:out": optOut,
		"opt:in": optIn,
		"reset": reset,
		"assign:group": assignUserToGroup,
	};
}

function trackEvent(event, data) {
	if (shouldTrackEvent(event, this._ignoredEvents)) {
		if (this._ready) {
			this._userMetrics.trackEvent(event, data);
		}
		else {
			this._eventQueue.push({ event, data });
		}
	}
}

// These are properties on the user object that will be sent with every event
function setUserProperty(property, value) {
	const identify = new Identify();
	identify.set(property, value);
	this._userMetrics.identifyUser(identify);
}

// Same as setUserProperty, but these properties cannot be changed once set
function setImmutableUserProperty(property, value) {
	const identify = new Identify();
	identify.setOnce(property, value);
	this._userMetrics.identifyUser(identify);
}

function assignUserToGroup(group, value) {
	this._userMetrics.assignUserToGroup(group, value);
}

function setUserId(id) {
	this._userMetrics.setUserId(id);
}

function reset() {
	this._userMetrics.reset();
}

function optOut() {
	this._userMetrics.setOptOutStatus(true);
}

function optIn() {
	this._userMetrics.setOptOutStatus(false);
}

function listeners() {
	return {
		// none yet
	};
}

async function refresh() {
	const userMetricsConfig = await this.api.configurations.userMetrics();
	const ignoredEventsList = userMetricsConfig.ignoredEvents || [];
	this._ignoredEvents = Array.isArray(this._ignoredEvents)
		? this._ignoredEvents.concat(ignoredEventsList)
		: ignoredEventsList;
}

function shouldTrackEvent(event, ignoredEvents) {
	return !ignoredEvents.includes(event);
}
